import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { debounce, DebouncedFunc } from 'lodash-es';
import Hyperbeam, {
  FailedToLoadError,
  HyperbeamEmbed,
  OfflineError,
  SessionTerminatedError,
  TimedOutError,
} from '@hyperbeam/web';
import PortalInstance, {
  FailedToLoadError as PortalFailedToLoadError,
  PortalEmbed as PortalEmbed,
  OfflineError as PortalOfflineError,
  TimedOutError as PortalTimedOutError,
  SessionTerminatedError as PortalSessionTerminatedError,
  FileListItem,
} from '@flowportals/portal-client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService } from 'src/app/services/auth.service';
import { HB_VM_STATE, HyperbeamService } from 'src/app/services/hyperbeam.service';
import {
  ISessionViewState,
  SessionSharedDataService,
  SessionView,
} from 'src/app/services/session-shared-data.service';
import { UserService } from 'src/app/services/user.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { RtcServiceController } from 'src/app/services/rtc.service';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  Subscription,
  skip,
  tap,
} from 'rxjs';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { modifiedSetTimeout } from 'src/app/utilities/ZoneUtils';
import { FeatureManagementService } from 'src/app/services/feature-management.service';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { MatDialog } from '@angular/material/dialog';
import { PortalFileManagerComponent } from 'src/app/standalones/components/portal-file-manager/portal-file-manager.component';
import { ScreenshotService } from '../../../../services/screenshot.service';
import { SpaceLeaderModeService } from '../../../../services/space-leader-mode.service';
import { FollowModeUiService } from '../../../../services/leader-follow-modes/follow-mode/follow-mode-ui.service';
import { SpaceZoomCutoffService } from '../../../../services/space-zoom-cutoff.service';
import { NetworkStatus, SessionsSyncService } from '../../../../services/sessions-sync.service';
import { DomListener } from '../../../../utilities/DomListener';
import { WebViewerPerformanceControlService } from '../../../../services/web-viewer-performance-control.service';
import { DeviceAndBrowserDetectorService } from '../../../../services/device-and-browser-detector.service';
import { FLAGS, FlagsService } from '../../../../services/flags.service';
import { SpaceBoardsService } from '../../../../services/space-boards.service';
import { AppsInteractionsLockService } from '../../item-wrapper/apps-interactions-lock.service';
import {
  AppIcon,
  AppIconSource,
  ItemsCanvasService,
} from '../../../../services/items-canvas.service';
import { WebViewerPausedModalType } from '../app-unavailable-performance-modal/web-viewer-performance-pause-modal.component';
import {
  AppConfigurationService,
  INTERACTIONS_BAR_HEIGHT,
  INTERACTIONS_BAR_PADDING,
  WEB_VIEWER_PADDING,
} from '../additional-apps.utils';
import { BoardItemsInteractionsManager } from '../../items-canvas/board-items-interactions-manager';
import { WebViewerConfigurations } from '../../items-canvas/board-items/board-web-viewer-configurartions';
import { AppsInsertionService } from '../../items-canvas/board-items/apps-insertion.service';
import { BoardItemsLoggingService } from '../../items-canvas/board-items/board-items-logging.service';
import { ItemModel } from '../../../../models/itemModel';
import { CustomerSupportService } from '../../../../services/customer-support.service';
import {
  isAppFullscreenedInCall,
  FullScreenAppManagerService,
} from '../../services/full-screen-app-manager.service';

enum HyperbeamState {
  NotInitialized = 'NotInitialized',
  Loading = 'Loading',
  NoError = 'NoError',
  TimedOut = 'TimedOut',
  InvalidParams = 'InvalidParams',
  SessionTerminated = 'SessionTerminated', // Indicates the VM was shutdown before initial load
  UnknownError = 'UnknownError',
  PotentiallyTerminated = 'PotentiallyTerminated', // this indicates that we should check if the session was terminated ourselves.
}

const RESIZE_DELAY = 200;
const WEBVIEWER_DEFAULT_VOLUME = 1; // Ranges from 0-1
const WEBVIEWER_DEFAULT_DESTROY_DELAY_MS = 60_000; // Default delayed-destroy timeout

@UntilDestroy()
@Component({
  selector: 'app-web-viewer',
  templateUrl: './web-viewer.component.html',
  styleUrls: ['./web-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebViewerComponent {
  @Input() state!: WebViewerConfigurations;
  @Input() static!: boolean;
  @Input() privateMode = false;

  @Output() stateChanged = new EventEmitter<WebViewerConfigurations>();
  @Output() deleteItem = new EventEmitter<void>();

  @ViewChild('webViewerEmbed') private webViewerEmbed?: ElementRef<HTMLDivElement>;
  @ViewChild('webViewerContainer') private webViewerContainer?: ElementRef<HTMLDivElement>;

  readonly hyperbeamState$ = new BehaviorSubject<HyperbeamState>(HyperbeamState.NotInitialized);

  readonly isWebViewerEnabledForCurrentSpace$ =
    this.featureManagementService.isWebViewerEnabled$.pipe(
      untilDestroyed(this),
      tap((webViewerCapabilityEnabled) => {
        if (!webViewerCapabilityEnabled) {
          this.hideLoadingIndicator();
        }
      }),
    );

  readonly isSpaceOwner$ = this.spaceRepo.isCurrentUserOwnerOfSpace$;
  readonly shouldPauseForSessionView$ = new BehaviorSubject<boolean>(false);
  readonly hyperbeamSubject$ = new BehaviorSubject<HyperbeamEmbed | PortalEmbed | undefined>(
    undefined,
  );
  readonly shouldPauseWebViewerForPerformance$ = new BehaviorSubject<boolean>(false);
  readonly userOffline$ = new BehaviorSubject<boolean>(false);
  readonly isReloading$ = new BehaviorSubject<boolean>(false);
  readonly showLoadingIndicator$ = new BehaviorSubject<boolean>(true);
  readonly webViewerAvailable$ = new BehaviorSubject<boolean>(false);
  readonly shouldDisableInteractions$ = new BehaviorSubject<boolean>(false);
  readonly canReloadWebViewerAutomatically$ = new BehaviorSubject<boolean>(false);
  readonly selected$ = new BehaviorSubject<boolean>(false);
  // Auto-recovery (dead/dropped portal → reload without the "Reload session"
  // overlay). Single-flight guard + cap so a persistently-broken portal can't
  // spin forever; after MAX_AUTO_RECOVERY_ATTEMPTS the overlay button reappears.
  private autoRecoveryActive = false;
  private readonly MAX_AUTO_RECOVERY_ATTEMPTS = 4;
  readonly editModeEnabled$ = new BehaviorSubject<boolean>(false);
  readonly privateMode$ = new BehaviorSubject<boolean>(true);
  readonly isPortalInstance$ = new BehaviorSubject<boolean>(false);
  readonly portalFiles$ = new BehaviorSubject<FileListItem[]>([]);
  readonly hasPortalFiles$ = this.portalFiles$.pipe(map((files) => files.length > 0));
  readonly hasNewFiles$ = new BehaviorSubject<boolean>(false);
  readonly isFileManagerEnabled$ = new BehaviorSubject<boolean>(
    this.flagsService.isFlagEnabled(FLAGS.WEB_VIEWER_FILE_MANAGER),
  );
  private lastSeenFileCount = 0;
  private fileManagerDialogRef?: any;
  readonly appIcon$ = new BehaviorSubject<AppIcon>({
    type: AppIconSource.ICON,
    content: '',
    height: 0,
    width: 0,
  });

  readonly ItemModel = ItemModel;
  readonly HyperbeamState = HyperbeamState;
  readonly WebViewerPausedModalType = WebViewerPausedModalType;
  readonly sessionView = SessionView;
  readonly document = document;
  readonly isFullscreenAppInCall$ = this.fullScreenAppManagerService.isFullScreeningAppInCall$();

  zoomCutOffSubscription?: Subscription;
  webViewerSessionViewSubscription?: Subscription;
  webViewerNetWorkConnectivitySubscription?: Subscription;
  webViewerPerformancePauseSubscription?: Subscription;
  webViewerPauseSubscription?: Subscription;

  lastTerminatedSessionId: string | undefined;
  unloaded = false;
  resizeObserver?: ResizeObserver;
  intersectionObserver?: IntersectionObserver;
  debouncedResize?: DebouncedFunc<any>;

  lastAttemptedHyperbeamSessionId?: string;
  isWebViewerVisible = false;
  private isWebViewerIntersecting = false;
  private pendingDestroyTimeoutId?: any;
  private loadingIndicatorHideTimeoutId?: any;

  domListener: DomListener;

  shouldRunAppOnMobile?: boolean;

  disableReasonMessage$: Observable<string>;

  get isVideoPaused(): boolean {
    return this.hyperbeam?.videoPaused === true;
  }

  get isAudioPaused(): boolean {
    return this.hyperbeam?.volume === 0;
  }

  get hyperbeam(): HyperbeamEmbed | PortalEmbed | undefined {
    return this.hyperbeamSubject$.value;
  }

  set hyperbeam(value: HyperbeamEmbed | PortalEmbed | undefined) {
    this.hyperbeamSubject$.next(value);
  }

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private hyperbeamService: HyperbeamService,
    private sharedDataService: SessionSharedDataService,
    public spaceRepo: SpaceRepository,
    public spaceBoardsService: SpaceBoardsService,
    public rtcServiceController: RtcServiceController,
    private telemetry: TelemetryService,
    private screenshotService: ScreenshotService,
    private zone: NgZone,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private followModeUiService: FollowModeUiService,
    private spaceCutOffService: SpaceZoomCutoffService,
    private sessionSyncService: SessionsSyncService,
    private webViewerPerformanceControlService: WebViewerPerformanceControlService,
    private domListenerFactoryService: DomListenerFactoryService,
    private flagsService: FlagsService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private appsInteractionsLockService: AppsInteractionsLockService,
    private itemsCanvasService: ItemsCanvasService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private changeDetectorRef: ChangeDetectorRef,
    private appConfigurationService: AppConfigurationService,
    private webViewerService: AppsInsertionService,
    private boardItemsLoggingService: BoardItemsLoggingService,
    protected featureManagementService: FeatureManagementService,
    private translateService: TranslateService,
    private customerSupportService: CustomerSupportService,
    private dialog: MatDialog,
    private fullScreenAppManagerService: FullScreenAppManagerService,
  ) {
    combineLatest([this.webViewerAvailable$, this.spaceLeaderModeService.currentUserIsBeingLed$])
      .pipe(
        untilDestroyed(this),
        map(
          ([webViewerAvailable, currentUserIsBeingLed]) =>
            webViewerAvailable && !currentUserIsBeingLed,
        ),
      )
      .subscribe(this.canReloadWebViewerAutomatically$);
    this.domListener = this.domListenerFactoryService.createInstance();
    // Hide loading indicator screen
    if (!this.featureManagementService.isWebViewerEnabled()) {
      this.hideLoadingIndicator();
    }

    this.disableReasonMessage$ = combineLatest([
      this.featureManagementService.currentSpaceBelongToFreeGuestUser$,
      this.spaceRepo.isCurrentUserOwnerOfSpace$,
    ]).pipe(
      untilDestroyed(this),
      map(([belongsToFreeGuestUser, isSpaceOwner]) =>
        belongsToFreeGuestUser
          ? isSpaceOwner
            ? this.translateService.instant(
                'This feature is only available to Pro or Enterprise users.',
              )
            : this.translateService.instant(
                'The owner of this Space must be a Pro or Enterprise user to use this feature.',
              )
          : isSpaceOwner
          ? this.translateService.instant(
              'This feature is not available on your current tier. Please contact support.',
            )
          : this.translateService.instant(
              'The owner of this Space must change their tier to use this feature.',
            ),
      ),
      distinctUntilChanged(),
    );
  }

  ngOnInit() {
    // Do not register subscriptions for static canvas item
    if (this.static) {
      return;
    }

    this.shouldRunAppOnMobile = this.getShouldRunOnMobile();
    if (!this.shouldRunAppOnMobile) {
      return;
    }

    this.setUpMouseAndKeyboardInteractions();
    this.setUpSelectionObservables();
  }

  async ngAfterViewInit() {
    const icon = this.appConfigurationService.getAppByUid(this.state.appInstanceId)?.icon ?? '';

    this.appIcon$.next({
      type: this.itemsCanvasService.getIconSource(icon),
      content: icon,
      height: 32,
      width: 32,
    });

    this.privateMode$.next(this.privateMode);

    if (!this.shouldRunAppOnMobile || this.static) {
      return;
    }

    if (this.state?.uid) {
      this.setupFullScreenAppStateAvailabilitySync();
    }

    this.setupWebViewerSuspensionLogic();
    this.appsInteractionsLockService.onInteractionsLockChanged(
      this.state.interactionsLocked,
      this.state.uid,
    );
  }

  ngOnDestroy(): void {
    this.unloaded = true; // mark this component as unloaded
    this.closeTransientOverlays();
    this.cancelPendingDestroy();
    this.cancelLoadingIndicatorHide();
    this.destroyWebViewer();
    this.enableWebViewerInsertion();
    this.domListener.clear();
    this.intersectionObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.privateMode$.next(this.privateMode);

    if (!changes.state?.previousValue || !changes.state?.currentValue) {
      return;
    }

    const previousConfig = changes.state.previousValue as WebViewerConfigurations;
    const currentConfig = changes.state.currentValue as WebViewerConfigurations;

    if (
      previousConfig.hyperbeamSessionId !== currentConfig.hyperbeamSessionId ||
      previousConfig.embedUrl !== currentConfig.embedUrl
    ) {
      this.reloadWebViewer(false, true); // force reload the local web viewer
    }

    if (previousConfig.interactionsLocked !== currentConfig.interactionsLocked) {
      this.appsInteractionsLockService.onInteractionsLockChanged(
        this.state.interactionsLocked,
        this.state.uid,
      );
    }
  }

  onSelectionChanged() {
    if (this.selected$.getValue()) {
      this.webViewerPerformanceControlService.setActiveWebViewerCanvasItemId(this.state.uid);
    }
  }

  private setUpMouseAndKeyboardInteractions() {
    combineLatest([
      this.hyperbeamState$,
      this.appsInteractionsLockService.interactionsLockState$(this.state.uid),
      this.editModeEnabled$,
      this.sharedDataService.sessionView.current$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([hyperbeamState, interactionsLockState, editModeEnabled, sessionViewState]) => {
        // Those are not all the errors, but are all the errors that the user should try to recover from them by themselves
        const webViewerHaveReloadableError = [
          HyperbeamState.TimedOut,
          HyperbeamState.InvalidParams,
          HyperbeamState.UnknownError,
        ].includes(hyperbeamState);
        this.shouldDisableInteractions$.next(
          interactionsLockState.interactionsDisabledForUser &&
            // No need to prevent interactions as content is not loaded to the screen,
            // and we need to allow the user to reload the web viewer to recover from the errors
            !webViewerHaveReloadableError,
        );

        // Check if this specific webviewer is currently in fullscreen mode
        const isThisWebViewerFullScreened = this.isWebViewerFullscreened(this.state.uid);

        if (this.hyperbeam) {
          // Enable delegateKeyboard when in edit mode OR when this webviewer is fullscreened
          // This allows followers in leader mode to use keyboard when the webviewer is fullscreened
          this.hyperbeam.delegateKeyboard =
            !this.shouldDisableInteractions$.getValue() &&
            (editModeEnabled || isThisWebViewerFullScreened);
        }

        if (this.shouldDisableInteractions$.getValue()) {
          this.webViewerEmbed?.nativeElement?.blur && this.webViewerEmbed.nativeElement.blur();
        }
      });
  }

  private setUpSelectionObservables() {
    this.boardItemsInteractionsManager
      .itemSelected$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe((selected) => {
        this.selected$.next(selected);
        this.onSelectionChanged();
        this.changeDetectorRef.detectChanges();
      });

    this.boardItemsInteractionsManager
      .itemInEditMode$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe((editModeEnabled) => {
        this.editModeEnabled$.next(editModeEnabled);
        this.changeDetectorRef.detectChanges();
      });
  }

  private setupFullScreenAppStateAvailabilitySync(): void {
    this.webViewerAvailable$
      .pipe(skip(1), distinctUntilChanged(), untilDestroyed(this))
      .subscribe((available) => {
        this.registerFullScreenAppState(available);
      });
  }

  private closeTransientOverlays(): void {
    this.zone.run(() => {
      this.closeFileManager();
    });
  }

  private subscribeToWebViewerViewportActivity(): void {
    if (!this.isValidWebViewer() || !this.webViewerContainer?.nativeElement) {
      return;
    }

    const webViewerElement = this.webViewerContainer.nativeElement;

    // Run outside zone to reduce unnecessary CDs
    this.zone.runOutsideAngular(() => {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isWebViewerIntersecting = entry.isIntersecting;
            this.handleViewportActivity();
          });
        },
        {
          root: null, // Use viewport as root
          threshold: 0, // Trigger as soon as any part is visible
        },
      );
      this.intersectionObserver.observe(webViewerElement);

      // Still need to subscribe to cutoff changes as those affect visibility
      this.zoomCutOffSubscription?.unsubscribe();
      this.zoomCutOffSubscription = this.spaceCutOffService.shouldCutOff$
        .pipe(untilDestroyed(this))
        .subscribe(() => this.handleViewportActivity());
    });
  }

  private async handleViewportActivity() {
    this.isWebViewerVisible = this.isWebViewerInViewport() && !this.spaceCutOffService.shouldCutOff;
    if (this.isWebViewerVisible) {
      // cancel any scheduled destruction when it re-enters viewport
      this.cancelPendingDestroy();
      // reload the webviewer if needed, or mark it as active
      if (this.webViewerNeedsReload()) {
        console.log(
          `Web Viewer: Reloading viewer on viewport activity; session id: ${this.state.hyperbeamSessionId}`,
        );
        // Only create a new HB session if the current session was supsended/terminated, otherwise just
        // load existing session (which wasn't intialized as web viewer was out of viewport).
        const isInitialLoad = this.hyperbeamState$.getValue() === HyperbeamState.NotInitialized;
        const createNewSession = !isInitialLoad && this.canReloadWebViewerAutomatically$.getValue();
        await this.reloadWebViewer(createNewSession);

        // The first load attempt embeds whatever session id was already in the state, which may have
        // been suspended/terminated in the meantime. In that case, create a fresh session right away
        // instead of waiting for the web viewer to leave and re-enter the viewport.
        await this.reloadSuspendedWebViewerIfOnInitialLoad(isInitialLoad);
      } else {
        this.hyperbeam?.ping(); // update inactive timeout only for Hyperbeam instances
      }
      return;
    }

    if (this.hyperbeamState$.getValue() !== HyperbeamState.NoError) {
      return; // no need to destroy as it is using resources
    }
    // Running webview went out of viewport; schedule shutdown with optional delay
    if (this.isDelayedDestroyEnabled()) {
      this.scheduleDelayedDestroy();
    } else {
      console.log(
        `Web Viewer: Destroy viewer on leaving viewport (no delay); session id: ${this.state.hyperbeamSessionId}`,
      );
      this.destroyWebViewer();
    }
  }

  private async reloadSuspendedWebViewerIfOnInitialLoad(onInitialLoad: boolean) {
    if (
      onInitialLoad &&
      this.hyperbeamState$.getValue() === HyperbeamState.SessionTerminated &&
      this.canReloadWebViewerAutomatically$.getValue()
    ) {
      console.log(
        `Web Viewer: Reloading suspended viewer on initial load; session id: ${this.state.hyperbeamSessionId}`,
      );
      await this.reloadWebViewer(true);
    }
  }

  private subscribeToWebViewerNetworkConnectivity(): void {
    this.webViewerNetWorkConnectivitySubscription?.unsubscribe();
    this.webViewerNetWorkConnectivitySubscription = this.sessionSyncService.networkStatusObservable$
      .pipe(
        untilDestroyed(this),
        filter((networkStatus) => networkStatus === NetworkStatus.CONNECTED),
        // the connected status doesn't mean that services can connect immediately,
        // it may require more time to have a stable network connection
        debounceTime(1000),
      )
      .subscribe(() => this.scheduleAutoRecovery());
  }

  private subscribeToSessionView(): void {
    this.webViewerSessionViewSubscription?.unsubscribe();
    this.webViewerSessionViewSubscription = this.sharedDataService.sessionView.current$
      .pipe(untilDestroyed(this))
      .subscribe((viewState: ISessionViewState) => {
        const currentView = viewState.view;
        const isWebViewerCallFullscreen = this.isWebViewerFullscreened(this.state.uid);

        const shouldPauseForSessionView =
          [SessionView.GALLERY_VIEW, SessionView.FULLSCREEN, SessionView.EXPANDED].includes(
            currentView,
          ) && !isWebViewerCallFullscreen;
        this.shouldPauseForSessionView$.next(shouldPauseForSessionView);

        if (
          (currentView === SessionView.FULLSCREEN_APP || isWebViewerCallFullscreen) &&
          viewState.sessionViewMetaData?.fullscreenId === this.state.uid
        ) {
          this.setItemAsPerformanceActiveWebViewer();
        }
        // to close the file manager when opened and a leader changes the current session view
        this.closeTransientOverlays();
      });
  }

  private pauseWebViewerVideo() {
    if (this.hyperbeam && !this.isVideoPaused) {
      this.hyperbeam.videoPaused = true;
      console.log(
        `Web Viewer: Pausing viewer video (current view: ${this.sharedDataService.sessionView.getSessionView()}); session id: ${
          this.state.hyperbeamSessionId
        }`,
      );
    }
  }

  private resumeWebViewerVideo() {
    if (this.hyperbeam && this.isVideoPaused) {
      this.hyperbeam.videoPaused = false;
      console.log(
        `Web Viewer: Resuming viewer video (current view: ${this.sharedDataService.sessionView.getSessionView()}); session id: ${
          this.state.hyperbeamSessionId
        }`,
      );
    }
  }

  private muteWebViewer() {
    if (this.hyperbeam && !this.isAudioPaused) {
      this.hyperbeam.volume = 0;
    }
  }

  private unmuteWebViewer() {
    if (this.hyperbeam && this.isAudioPaused) {
      this.hyperbeam.volume = WEBVIEWER_DEFAULT_VOLUME;
    }
  }

  private isValidWebViewer() {
    return Boolean(!this.static);
  }

  private isWebViewerInViewport() {
    return this.isWebViewerIntersecting;
  }

  private webViewerNeedsReload() {
    // If the webviewer is paused for performance reasons we should not reload
    if (this.shouldPauseWebViewerForPerformance$.value) {
      return false;
    }

    // Web Viewer needs a reload if:
    // 1. It is currently not loaded (state === NotInitialized), OR
    // 2. It is currently in a reloadable state (NotInitialized, Suspended, Terminated), AND
    //    ( Either this user is a host [so they can create a new session] OR The WebViewerState has changed [remote host may have started a new session] )
    if (this.hyperbeamState$.getValue() === HyperbeamState.NotInitialized) {
      return true;
    }

    const webviewReloadable = [
      HyperbeamState.SessionTerminated,
      HyperbeamState.PotentiallyTerminated,
    ].includes(this.hyperbeamState$.getValue());
    return webviewReloadable && (this.canReloadWebViewer() || this.webViewerStateChanged());
  }

  private canReloadWebViewer(): boolean {
    return this.webViewerAvailable$.getValue();
  }

  private async loadWebViewer(initialLoad = false) {
    if (
      this.isValidWebViewer() &&
      this.isWebViewerInViewport() &&
      !this.shouldPauseWebViewerForPerformance$.value
    ) {
      if (this.hyperbeamState$.getValue() === HyperbeamState.Loading) {
        console.log(
          `Web Viewer: Viewer already loading, ignoring parallel load request; session id: ${this.state.hyperbeamSessionId}`,
        );
        return;
      }
      this.hyperbeamState$.next(HyperbeamState.Loading);
      // set the last (attempted) loaded state
      this.lastAttemptedHyperbeamSessionId = this.state.hyperbeamSessionId;

      const embedURL = this.state.embedUrl;
      const container = this.webViewerEmbed?.nativeElement;
      if (embedURL && container) {
        let sanitizedEmbedURl = this.sanitizeHyperbeamUrl(embedURL);
        const isPortalSession = this.state.isPortalSession;
        const bypassFlowportalsDomainEnabled = this.flagsService.isFlagEnabled(
          FLAGS.BYPASS_FLOWPORTALS_DOMAIN,
        );
        if (isPortalSession && bypassFlowportalsDomainEnabled) {
          sanitizedEmbedURl = this.sanitizePortalUrl(sanitizedEmbedURl);
        }
        this.hyperbeamState$.next(await this.embedHyperbeam(container, sanitizedEmbedURl));
        this.createResizeObserver();
      }

      // NOTE: auto-recovery from a terminated/dropped viewer is NOT triggered here.
      // Doing it inline is racy — loadWebViewer runs INSIDE reloadWebViewer for the
      // viewport-activity path, so isReloading$ is set and any recovery kicked off
      // here either re-enters or is skipped (that was the bug: dead portals stayed
      // on the "Reload session" button). Recovery is now driven from the failure
      // callbacks (onHyperbeamEmbedFailed / onHyperbeamDisconnected) via
      // runAutoRecovery(), deferred so it runs AFTER the current reload settles.
      await this.reloadSuspendedWebViewerIfOnInitialLoad(initialLoad);
    }
  }

  // Rewrite the hyperbeam.com domain to vm.pencilapp.com to avoid domain based blacklists
  private sanitizeHyperbeamUrl(hbUrl: string) {
    return hbUrl.replace('hyperbeam.com', 'vm.pencilapp.com');
  }

  // Rewrite the flowportals.com domain to wv.pencilapp.com to avoid domain based blacklists (when BYPASS_FLOWPORTALS_DOMAIN flag is enabled)
  private sanitizePortalUrl(portalUrl: string) {
    return portalUrl.replace('flowportals.com', 'wv.pencilapp.com');
  }

  private async embedHyperbeam(container: HTMLDivElement | HTMLIFrameElement, embedURL: string) {
    return this.zone.runOutsideAngular(async () => {
      const sessionId = this.state.hyperbeamSessionId;
      try {
        const userToken = (await this.authService.currentToken()) ?? '';
        const userEmail = this.userService.user.value?.user.email ?? '';
        console.log(`Web Viewer: Start embedding new viewer; session id: ${sessionId} `);
        this.hyperbeam?.destroy();
        const config = {
          webhookUserdata: {
            email: userEmail,
            token: userToken,
          },
          delegateKeyboard: false, // don't forward keyboard events if text fields etc. are not in focus
          videoPaused: false, // wait for the first frame, before pausing the video
          persistScreen: true, // display a screenshot for an inactive session
          onConnectionStateChange: (event: any) => this.onHyperbeamConnectionStateChanged(event),
          onDisconnect: (event: any) => this.onHyperbeamDisconnected(event),
          onFileListChange: (event: {
            files: FileListItem[];
            previousFiles: FileListItem[];
            cwd: string;
          }) => this.onPortalFileListChange(event),
        };
        const isPortalSession = this.state.isPortalSession;

        if (isPortalSession !== undefined && isPortalSession) {
          this.hyperbeam = await PortalInstance(container, embedURL, config);
        } else {
          this.hyperbeam = await Hyperbeam(container, embedURL, config);
        }

        return this.onHyperbeamEmbedded();
      } catch (err) {
        return this.onHyperbeamEmbedFailed(err, sessionId);
      }
    });
  }

  onHyperbeamEmbedded() {
    console.log(
      `Web Viewer: Finished embedding new viewer; session id: ${this.state.hyperbeamSessionId} `,
    );

    if (this.isPortalSession()) {
      this.telemetry.event('Portal WebViewer Embedded');
    }

    this.enableWebViewerInsertion();

    // Update Portal instance detection
    this.isPortalInstance$.next(this.isPortalInstance(this.hyperbeam));

    // Initialize file list for Portal instances
    if (this.isPortalInstance(this.hyperbeam)) {
      (this.hyperbeam as PortalEmbed)
        .listFiles()
        .then((files) => {
          this.portalFiles$.next(files || []);
          this.lastSeenFileCount = files?.length || 0;
          this.updateHasNewFiles();
        })
        .catch((error) => {
          // Log error to Sentry - file list will be loaded when user opens modal
          // but we need to track failures to diagnose UI state issues
          captureException(error, {
            tags: {
              component: 'WebViewerComponent',
              action: 'initializeFileList',
            },
            extra: {
              hyperbeamSessionId: this.state.hyperbeamSessionId,
            },
          });
        });
    }

    // There is a possible race-condition here since creating Hyperbeam returns a promise, it is possible
    // that this component can get destroyed before the embed completes. We check if this is the case,
    // and perform the necessary cleanup.
    if (this.unloaded) {
      this.destroyWebViewer();
    } else {
      this.setInitialZoomIfExists();
    }

    return HyperbeamState.NoError;
  }

  onHyperbeamDisconnected(event: { type: any }): void {
    if (event.type === 'portal-connection-died') {
      this.hyperbeamState$.next(HyperbeamState.TimedOut);

      if (this.isPortalSession()) {
        this.telemetry.errorEvent('Portal WebViewer Connection Died');
      }
    } else {
      this.hyperbeamState$.next(HyperbeamState.PotentiallyTerminated);
    }
    this.hideLoadingIndicator();
    console.log(
      `Web Viewer: event type: ${event.type}; session id: ${this.state.hyperbeamSessionId}`,
    );

    // Auto-recover from a live disconnect/termination the moment the SDK signals
    // it — instead of leaving the "Reload session" overlay for the user to click.
    // Runs inside the Angular zone (this fires from the SDK callback, outside it)
    // and DEFERRED so it starts after any in-flight reload settles. runAutoRecovery
    // is single-flight, viewport-aware, capped + backed-off, forces a NEW session
    // for a definitively-dead VM, and lets the overlay button reappear only if it
    // exhausts.
    this.zone.run(() => this.scheduleAutoRecovery());
  }

  onHyperbeamConnectionStateChanged(event: { state: string }): void {
    if (this.isPortalSession()) {
      this.telemetry.event(`Portal WebViewer Connection State Changed: ${event.state}`);
    }

    if (event.state === 'connecting' || event.state === 'reconnecting') {
      this.hyperbeamState$.next(HyperbeamState.Loading);
      this.showWebViewerLoadingIndicator();
    } else if (event.state === 'playing') {
      this.hyperbeamState$.next(HyperbeamState.NoError);
      this.hideLoadingIndicator();
      this.boardItemsLoggingService.reportWebViewerRendered(this.state.uid);
      this.updateFullscreenAppTileState();
    }
    console.log(
      `Web Viewer: connection state: ${event.state}; session id: ${this.state.hyperbeamSessionId}`,
    );
  }

  onHyperbeamEmbedFailed(err: any, sessionId: string | undefined) {
    this.userOffline$.next(err instanceof OfflineError || err instanceof PortalOfflineError);

    // Map errors
    let hbErr;
    // The order of checking SessionTerminatedError and FailedToLoadError matters,
    // because SessionTerminatedError extends FailedToLoadError
    if (err instanceof SessionTerminatedError || err instanceof PortalSessionTerminatedError) {
      this.lastTerminatedSessionId = sessionId;
      hbErr = HyperbeamState.SessionTerminated;
    } else if (
      err instanceof TimedOutError ||
      err instanceof PortalTimedOutError ||
      err instanceof OfflineError ||
      err instanceof PortalOfflineError
    ) {
      hbErr = HyperbeamState.TimedOut;
    } else if (err instanceof FailedToLoadError || err instanceof PortalFailedToLoadError) {
      hbErr = HyperbeamState.PotentiallyTerminated;
    } else if (err instanceof TypeError) {
      hbErr = HyperbeamState.InvalidParams;
    } else {
      hbErr = HyperbeamState.UnknownError;
    }
    console.log(
      `Web Viewer error: ${hbErr} [${err.name}] - ${err.message}; session id: ${this.state.hyperbeamSessionId}`,
    );

    if (this.isPortalSession()) {
      this.telemetry.errorEvent('Portal WebViewer Embed Failed');
    } else {
      this.telemetry.errorEvent('Hyperbeam Embed Failed', {
        error: hbErr,
        name: err.name,
        message: err.message,
        hbSessionId: this.state.hyperbeamSessionId,
        isPortalSession: this.isPortalSession(),
      });
    }

    this.enableWebViewerInsertion();
    this.hideLoadingIndicator();

    // Embed failed → the viewer is in a bad state (SessionTerminated for a dead
    // VM, PotentiallyTerminated / TimedOut otherwise). Kick off auto-recovery,
    // DEFERRED: hbErr is applied to hyperbeamState$ by the CALLER after this
    // returns, and if we're inside a reloadWebViewer the reload must settle first.
    // runAutoRecovery re-reads the state, is single-flight, and no-ops for the
    // non-recoverable states (InvalidParams / UnknownError).
    this.scheduleAutoRecovery();
    return hbErr;
  }

  private updateFullscreenAppTileState(): void {
    const video = this.getCurrentVideoElement();
    if (video) {
      this.fullScreenAppManagerService.updateFullscreenAppTileState(this.state.uid);
    }
  }

  webViewerStateChanged() {
    return this.state.hyperbeamSessionId !== this.lastAttemptedHyperbeamSessionId;
  }

  enableWebViewerInsertion(): void {
    this.webViewerService.enableWebViewerInsertion();
  }

  private destroyWebViewer() {
    if (this.hyperbeam) {
      console.log(`Web Viewer: Destroying embed; session id: ${this.state.hyperbeamSessionId} `);
      this.hyperbeam?.destroy();
      this.hyperbeam = undefined;
    }
    if (this.webViewerEmbed?.nativeElement) {
      this.removeAllChildNodes(this.webViewerEmbed.nativeElement);
    }
    this.destroyResizeObserver();
    this.hyperbeamState$.next(HyperbeamState.NotInitialized);
    this.isPortalInstance$.next(false);
    this.portalFiles$.next([]);
    this.hasNewFiles$.next(false);
    this.lastSeenFileCount = 0;
    this.boardItemsLoggingService.reportWebViewerDestroyed(this.state.uid);
  }

  private isDelayedDestroyEnabled(): boolean {
    return this.flagsService.isFlagEnabled(FLAGS.WEB_VIEWER_DELAYED_DESTROY);
  }

  private getDestroyDelayMs(): number {
    const defaultDelayMs = WEBVIEWER_DEFAULT_DESTROY_DELAY_MS;
    const vars = this.flagsService.featureFlagsVariables[FLAGS.WEB_VIEWER_DELAYED_DESTROY];
    const configured = vars?.destroy_delay_ms;
    return typeof configured === 'number' && configured >= 0 ? configured : defaultDelayMs;
  }

  private scheduleDelayedDestroy() {
    if (this.pendingDestroyTimeoutId) {
      return; // already scheduled
    }
    const delay = this.getDestroyDelayMs();
    console.log(
      `Web Viewer: Scheduling destroy in ${delay}ms; session id: ${this.state.hyperbeamSessionId}`,
    );
    this.pendingDestroyTimeoutId = modifiedSetTimeout(
      () => {
        this.pendingDestroyTimeoutId = undefined;
        if (this.hyperbeamState$.getValue() !== HyperbeamState.NoError) {
          console.log(
            `Web Viewer: Delayed destroy canceled as it is already destroyed; session id: ${this.state.hyperbeamSessionId}`,
          );
          return; // already destroyed;
        }

        // Double check still out of viewport and not paused for performance before destroying
        const stillNotVisible =
          !this.isWebViewerInViewport() || this.spaceCutOffService.shouldCutOff;

        if (stillNotVisible) {
          console.log(
            `Web Viewer: Delayed destroy fired; session id: ${this.state.hyperbeamSessionId}`,
          );
          this.destroyWebViewer();
        } else {
          console.log(
            `Web Viewer: Delayed destroy canceled due to re-entry; session id: ${this.state.hyperbeamSessionId}`,
          );
        }
      },
      delay,
      true,
    );
  }

  private cancelPendingDestroy() {
    if (this.pendingDestroyTimeoutId) {
      clearTimeout(this.pendingDestroyTimeoutId);
      this.pendingDestroyTimeoutId = undefined;
      console.log(
        `Web Viewer: Canceled scheduled destroy; session id: ${this.state.hyperbeamSessionId}`,
      );
    }
  }

  private removeAllChildNodes(parent?: HTMLDivElement) {
    if (parent) {
      while (parent.firstChild) {
        parent.removeChild(parent.firstChild);
      }
    }
  }

  private getCurrentVideoElement(): HTMLVideoElement | undefined | null {
    const container = this.webViewerEmbed?.nativeElement;
    let videoElement: HTMLVideoElement | null | undefined = null;
    if (container?.shadowRoot) {
      videoElement = container?.shadowRoot.querySelector('video');
    } else {
      videoElement = container?.querySelector('video');
    }
    return videoElement;
  }

  private isWebViewerResizableView() {
    const sessionView = this.sharedDataService.sessionView.getCurrentSessionViewState();
    const isWebViewerCallFullscreen = this.isWebViewerFullscreened(this.state.uid);
    return sessionView.view === SessionView.WHITEBOARD || isWebViewerCallFullscreen;
  }

  private resizeFunction(ro: ResizeObserverEntry) {
    // only resize web viewer when on Whiteboard view or FullScreen App (as otherwise the size will be 0,0)
    if (
      this.webViewerEmbed &&
      this.hyperbeam &&
      this.isWebViewerResizableView() &&
      ro.contentRect.width > 0 &&
      ro.contentRect.height > 0
    ) {
      this.resizeHyperbeamToDimensions(ro.contentRect.width, ro.contentRect.height);
    }
  }

  private resizeHyperbeamToDimensions(desiredWidth: number, desiredHeight: number): void {
    if (!this.hyperbeam || desiredWidth <= 0 || desiredHeight <= 0) {
      return;
    }
    const pixels = Math.min(desiredWidth * desiredHeight, this.hyperbeam.maxArea);
    const aspectRatio = desiredWidth / desiredHeight;
    const height = Math.floor(Math.sqrt(pixels / aspectRatio));
    const width = Math.floor(height * aspectRatio);
    const hyperBeamSizeLimit = 3840;
    if (width > 0 && width < hyperBeamSizeLimit && height > 0 && height <= hyperBeamSizeLimit) {
      this.hyperbeam.resize(width, height);
    }
  }

  private isWebViewerFullscreened(uid: string): boolean {
    const sessionViewState = this.sharedDataService.sessionView.getCurrentSessionViewState();
    return (
      (sessionViewState.view === SessionView.FULLSCREEN_APP &&
        sessionViewState.sessionViewMetaData?.fullscreenId === uid) ||
      isAppFullscreenedInCall(sessionViewState, uid)
    );
  }

  createResizeObserver() {
    // Create resize observer if loading browser succeeded
    if (this.hyperbeamState$.getValue() !== HyperbeamState.NoError) {
      return;
    }
    if (!this.debouncedResize) {
      this.debouncedResize = debounce(this.resizeFunction, RESIZE_DELAY);
    }
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      // This condition prevents the browser from resizing when:
      // 1. In leader mode: led users should not resize the browser to preserve the leader's original size
      // 2. In follow mode: followers should not resize the browser to preserve the followed user's original size
      // This ensures the shared browser dimensions are determined by the first/original viewer
      if (
        !this.spaceLeaderModeService.currentUserIsBeingLed() &&
        !this.followModeUiService.isFollowing()
      ) {
        this.debouncedResize(entries[0]);
      }
    });
    if (this.webViewerEmbed) {
      observer.observe(this.webViewerEmbed.nativeElement);
    }
    this.resizeObserver = observer;
  }

  private destroyResizeObserver() {
    this.resizeObserver?.disconnect();
  }

  async reloadWebViewer(createNewSession = false, forceReload = false) {
    if (
      !this.isValidWebViewer() ||
      this.isReloading$.getValue() ||
      !this.webViewerAvailable$.getValue()
    ) {
      return; // ignore if not a valid canvas item for web viewer, or already reloading
    }
    console.log(
      `Web Viewer: Reload called with new session: ${createNewSession}, forceReload: ${forceReload}`,
    );
    if (
      !this.webViewerNeedsReload() && // web viewer is up-to-date
      !forceReload
    ) {
      return;
    }
    this.isReloading$.next(true);
    this.showWebViewerLoadingIndicator();

    const shouldCheckForSessionTermination =
      this.hyperbeamState$.getValue() === HyperbeamState.PotentiallyTerminated;
    // The browser instance needs to be reloaded. Destroy it, create a new one, and embed the new one.
    this.destroyWebViewer();
    if (createNewSession || shouldCheckForSessionTermination) {
      await this.replaceWebViewerSession(shouldCheckForSessionTermination);
    }
    if (this.unloaded) {
      return;
    }
    await this.loadWebViewer();

    if (this.isPortalSession()) {
      this.telemetry.event('Portal WebViewer Reloaded');
    }

    this.isReloading$.next(false);
  }

  async handleWebViewerStaleSession() {
    if (!this.state.hyperbeamSessionId) {
      await this.replaceWebViewerSession();
    }
  }

  async replaceWebViewerSession(shouldCheckForSessionTermination = false) {
    if (!this.canReloadWebViewer()) {
      return;
    }
    // ensuring state consistency, no changes in the state between identifying it as terminated and reloading it
    if (
      this.hyperbeamState$.getValue() === HyperbeamState.SessionTerminated &&
      this.lastTerminatedSessionId !== this.state.hyperbeamSessionId
    ) {
      return;
    }
    try {
      // create a new session (restore old state if available)
      const hbSession = await this.hyperbeamService.createHbSession(
        this.state.hyperbeamSessionId,
        {
          webgl: this.state.webGLEnabled,
          defaultUrl: this.state.startUrl,
          country: this.state.country,
          adblock: this.state.adBlockEnabled,
          isPortalSession: this.state.isPortalSession,
        },
        shouldCheckForSessionTermination,
      );
      if (hbSession && hbSession.state === HB_VM_STATE.Alive && hbSession.data) {
        // update session_id and url and sync changes to other clients.
        console.log(
          `Web Viewer: Reloaded session id: ${this.state.hyperbeamSessionId}, new session id: ${hbSession.data.session_id}`,
        );
        this.state.embedUrl = hbSession.data.embed_url;
        this.state.hyperbeamSessionId = hbSession.data.session_id;
        this.state.isPortalSession = hbSession.data.is_portal_session ?? false;
        // Fresh session → fresh MCP token; sync it so the agent auths the new VM.
        this.state.remoteControlToken = hbSession.data.remote_control_token;
        if (this.state.isPortalSession) {
          this.state.webGLEnabled = true;
        }
        this.emitChanges();
      }
    } catch (error) {
      console.log(error);
      this.telemetry.errorEvent('Create Hyperbeam Session Failed', {
        error: error,
        hbSessionId: this.state.hyperbeamSessionId,
        webgl: this.state.webGLEnabled,
        defaultUrl: this.state.startUrl,
      });
    }
  }

  async takeScreenshot(): Promise<void> {
    if (!this.webViewerEmbed) {
      return;
    }

    const { WebViewerTop, WebViewerLeft, WebViewerWidth, WebViewerHeight } = {
      WebViewerTop: this.state.top,
      WebViewerLeft: this.state.left,
      WebViewerWidth: this.state.width,
      WebViewerHeight: this.state.height,
    };

    const webViewerOuterHeight =
      WebViewerHeight - INTERACTIONS_BAR_HEIGHT - 2 * INTERACTIONS_BAR_PADDING;
    const width = WebViewerWidth - 2 * WEB_VIEWER_PADDING;
    const height = webViewerOuterHeight - WEB_VIEWER_PADDING;

    const screenshotBoundingRect = {
      top: WebViewerTop + INTERACTIONS_BAR_HEIGHT + INTERACTIONS_BAR_PADDING,
      left: WebViewerLeft + WebViewerWidth,
      width: WebViewerWidth,
      height: WebViewerHeight - INTERACTIONS_BAR_HEIGHT - 2 * INTERACTIONS_BAR_PADDING,
    };

    const videoElement = this.getCurrentVideoElement();
    if (!videoElement) {
      return;
    }

    await this.screenshotService.takeScreenshot(
      videoElement,
      width,
      height,
      screenshotBoundingRect,
    );
  }

  hideLoadingIndicator(): void {
    this.cancelLoadingIndicatorHide();
    this.loadingIndicatorHideTimeoutId = modifiedSetTimeout(() => {
      this.loadingIndicatorHideTimeoutId = undefined;
      this.showLoadingIndicator$.next(false);
    }, 1000);
  }

  private showWebViewerLoadingIndicator(): void {
    // Cancel any pending hide from a previous attempt so it can't fire mid-reload and hide the
    // spinner (leaving a blank screen) while this new attempt is still in flight.
    this.cancelLoadingIndicatorHide();
    this.showLoadingIndicator$.next(true);
    this.startWebViewerReconnectingTimeout();
  }

  private cancelLoadingIndicatorHide(): void {
    if (this.loadingIndicatorHideTimeoutId) {
      clearTimeout(this.loadingIndicatorHideTimeoutId);
      this.loadingIndicatorHideTimeoutId = undefined;
    }
  }

  private startWebViewerReconnectingTimeout(): void {
    modifiedSetTimeout(() => {
      if (this.hyperbeamState$.getValue() === HyperbeamState.Loading) {
        this.hyperbeamState$.next(HyperbeamState.TimedOut);
        this.hideLoadingIndicator();
      }
    }, 18 * 1000); // This timeout is used to handle the case where the web viewer is not loaded after x seconds.
  }

  private isRecoverableState(s: HyperbeamState): boolean {
    return (
      s === HyperbeamState.SessionTerminated ||
      s === HyperbeamState.PotentiallyTerminated ||
      s === HyperbeamState.TimedOut
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => modifiedSetTimeout(() => resolve(), ms));
  }

  /** Start an auto-recovery cycle after the current call stack unwinds. DEFERRED
   *  on purpose: the failure callbacks fire from inside embed/reload, so we let
   *  hyperbeamState$ settle and any in-flight reloadWebViewer finish (isReloading$
   *  clears) before recovering — otherwise reloadWebViewer just early-returns and
   *  the viewer stays on the "Reload session" button (the bug we're fixing). */
  private scheduleAutoRecovery(): void {
    modifiedSetTimeout(() => void this.runAutoRecovery(), 0);
  }

  /** Automatically recover a viewer stuck in a recoverable-bad state without the
   *  user clicking the "Reload session" overlay. Single-flight; viewport-aware;
   *  capped with backoff. A definitively-dead VM (SessionTerminated) is recovered
   *  by forcing a NEW session (createNewSession=true) — re-embedding the dead
   *  session URL just 504s forever, which is exactly why the button used to stick.
   *  If every attempt fails the state stays bad and the overlay button appears as
   *  the manual fallback. */
  private async runAutoRecovery(): Promise<void> {
    if (this.autoRecoveryActive) {
      return; // a cycle already owns the retries
    }
    if (!this.canReloadWebViewerAutomatically$.getValue()) {
      return; // viewer unavailable or user is being led
    }
    if (!this.isRecoverableState(this.hyperbeamState$.getValue())) {
      return; // nothing to recover
    }
    this.autoRecoveryActive = true;
    try {
      let attempt = 0;
      while (attempt < this.MAX_AUTO_RECOVERY_ATTEMPTS) {
        if (this.unloaded || !this.canReloadWebViewerAutomatically$.getValue()) {
          return;
        }
        if (!this.isWebViewerVisible) {
          // Off-screen → mark uninitialized so it reloads when scrolled back in.
          this.hyperbeamState$.next(HyperbeamState.NotInitialized);
          return;
        }
        const state = this.hyperbeamState$.getValue();
        if (!this.isRecoverableState(state)) {
          return; // recovered (or moved to a non-recoverable error)
        }
        attempt += 1;
        const createNewSession = state === HyperbeamState.SessionTerminated;
        console.log(
          `Web Viewer: auto-recovery ${attempt}/${this.MAX_AUTO_RECOVERY_ATTEMPTS} ` +
            `(newSession=${createNewSession}, state=${state}); session id: ${this.state.hyperbeamSessionId}`,
        );
        // forceReload bypasses the up-to-date check; createNewSession replaces a
        // dead VM. No-ops if an external reload is briefly in flight — the backoff
        // + next attempt cover that.
        await this.reloadWebViewer(createNewSession, true);
        if (!this.isRecoverableState(this.hyperbeamState$.getValue())) {
          return; // success → Loading/NoError
        }
        await this.delay(5000); // backoff before the next attempt
      }
      console.log(
        `Web Viewer: auto-recovery exhausted after ${this.MAX_AUTO_RECOVERY_ATTEMPTS}; ` +
          `showing reload overlay; session id: ${this.state.hyperbeamSessionId}`,
      );
    } finally {
      this.autoRecoveryActive = false;
    }
  }

  private initializeWebViewerPerformancePauseLogic() {
    this.webViewerPerformancePauseSubscription?.unsubscribe();
    this.webViewerPerformancePauseSubscription = this.webViewerPerformanceControlService
      .shouldPauseWebViewer$(this.state.uid)
      .pipe(untilDestroyed(this))
      .subscribe(this.shouldPauseWebViewerForPerformance$);
  }

  setItemAsPerformanceActiveWebViewer(): void {
    this.webViewerPerformanceControlService.setActiveWebViewerCanvasItemId(this.state.uid);
  }

  private setupWebViewerPauseLogic() {
    // Unify the source of pausing/resuming the web viewer
    this.webViewerPauseSubscription?.unsubscribe();
    this.webViewerPauseSubscription = combineLatest([
      this.shouldPauseWebViewerForPerformance$,
      this.shouldPauseForSessionView$,
      this.hyperbeamSubject$,
    ])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        map(([pauseForPerformance, pauseForSessionView, hyperbeam]) => {
          const hyperbeamEmbedded = hyperbeam !== undefined;
          return [
            (pauseForPerformance || pauseForSessionView) && hyperbeamEmbedded, // shouldPauseVideo
            pauseForPerformance && hyperbeamEmbedded, // shouldPauseAudio
          ];
        }),
      )
      .subscribe(([shouldPauseVideo, shouldPauseAudio]) => {
        if (shouldPauseVideo) {
          this.pauseWebViewerVideo();
        } else {
          this.resumeWebViewerVideo();
        }

        if (shouldPauseAudio) {
          this.muteWebViewer();
        } else {
          this.unmuteWebViewer();
        }
      });
  }

  setupWebViewerSuspensionLogic(): void {
    combineLatest([
      this.webViewerAvailable$.pipe(distinctUntilChanged()),
      this.privateMode$.pipe(distinctUntilChanged()),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(async ([available, privateMode]) => {
        if (available && !privateMode) {
          await this.handleWebViewerStaleSession();
          await this.loadWebViewer(true);

          // Avoid setting up any listeners that may lead to leakage if the component was destroyed while loadWebViewer wasn't finished
          if (this.unloaded) {
            this.ngOnDestroy();
            return;
          }

          // subscribe to viewport activity and session view after initial browser load (attempt)
          this.subscribeToWebViewerViewportActivity();
          this.subscribeToSessionView();
          this.subscribeToWebViewerNetworkConnectivity();
          this.initializeWebViewerPerformancePauseLogic();
          this.setupWebViewerPauseLogic();
        } else {
          this.destroyWebViewer();
          this.removeWebViewerSubscriptions();
        }
      });
  }

  removeWebViewerSubscriptions(): void {
    this.zoomCutOffSubscription?.unsubscribe();
    this.webViewerSessionViewSubscription?.unsubscribe();
    this.webViewerNetWorkConnectivitySubscription?.unsubscribe();
    this.webViewerPerformancePauseSubscription?.unsubscribe();
    this.webViewerPauseSubscription?.unsubscribe();
    this.cancelPendingDestroy();
  }

  openTroubleShootLink() {
    this.customerSupportService.openLink(
      'https://intercom.help/pencil/en/articles/8508612-what-should-i-do-if-my-webviewer-isn-t-loading',
    );
  }

  setInitialZoomIfExists() {
    try {
      const initialZoom = this.state.initialZoom;
      if (this.hyperbeam && initialZoom && this.isHyperbeamInstance(this.hyperbeam)) {
        this.hyperbeam.tabs.setZoom(initialZoom);
      }
    } catch (e) {
      captureException(e);
    }
  }

  getShouldRunOnMobile(): boolean {
    return !(
      (this.deviceAndBrowserDetectorService.isMobile() &&
        this.flagsService.isFlagEnabled(FLAGS.DISABLE_APPS_ON_MOBILE) &&
        !this.isForcedToBeAvailableOnMobile(this.state.appInstanceId)) ??
      false
    );
  }

  isForcedToBeAvailableOnMobile(appId?: string): boolean {
    if (!appId) {
      return false;
    }
    return this.appConfigurationService.getAppByUid(appId)?.forceAvailableOnMobile || false;
  }

  onInteractionsLockChanges(interactionsLocked: boolean): void {
    this.state.interactionsLocked = interactionsLocked;
    this.appsInteractionsLockService.onInteractionsLockChanged(interactionsLocked, this.state.uid);
    this.emitChanges();
  }
  onPrivacyChanged(isPrivate: boolean): void {
    this.state.isPrivate = isPrivate;
    this.emitChanges();
  }
  onAvailabilityChanged(available: boolean): void {
    this.state.available = available;
    this.emitChanges();
  }

  onDeleteItem() {
    this.deleteItem.emit();
  }

  OnAvailabilityNotificationStatusChanged(hasAvailabilityNotification: boolean) {
    this.state.hasAvailabilityNotification = hasAvailabilityNotification;
    this.emitChanges();
  }

  emitChanges() {
    this.stateChanged.emit(this.state);
  }

  private registerFullScreenAppState(available = this.webViewerAvailable$.getValue()): void {
    if (!this.state?.uid) {
      return;
    }
    this.fullScreenAppManagerService.registerAppState(this.state, available);
  }

  // Add type guard to check instance type
  private isHyperbeamInstance(instance: HyperbeamEmbed | PortalEmbed): instance is HyperbeamEmbed {
    return instance instanceof Object && 'tabs' in instance;
  }

  /**
   * Helper method to check if the current webviewer is using Portal
   * Portal is our own service, while Hyperbeam is a third-party service
   */
  private isPortalSession(): boolean {
    return this.state.isPortalSession === true;
  }

  // Add type guard to check if instance is Portal
  private isPortalInstance(
    instance: HyperbeamEmbed | PortalEmbed | undefined,
  ): instance is PortalEmbed {
    if (!instance) {
      return false;
    }
    return !this.isHyperbeamInstance(instance);
  }

  openFileManager(): void {
    if (!this.isPortalInstance(this.hyperbeam)) {
      return;
    }

    if (!this.flagsService.isFlagEnabled(FLAGS.WEB_VIEWER_FILE_MANAGER)) {
      return;
    }

    // Update last seen count when opening modal (will clear new files indicator)
    this.lastSeenFileCount = this.portalFiles$.value.length;
    this.updateHasNewFiles();

    this.fileManagerDialogRef = this.dialog.open(PortalFileManagerComponent, {
      width: '450px',
      autoFocus: false,
      data: {
        portalEmbed: this.hyperbeam,
      },
    });

    // Reset last seen count when modal closes
    this.fileManagerDialogRef
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.lastSeenFileCount = this.portalFiles$.value.length;
        this.fileManagerDialogRef = undefined;
        // Recalculate hasNewFiles after closing modal
        this.updateHasNewFiles();
      });
  }

  private closeFileManager(): void {
    this.fileManagerDialogRef?.close();
  }

  /**
   * Centralized method to update hasNewFiles$ based on current file count vs lastSeenFileCount.
   * If modal is open, always sets to false (user is viewing files).
   * Otherwise, compares current file count with lastSeenFileCount.
   */
  private updateHasNewFiles(): void {
    const isModalOpen = this.fileManagerDialogRef !== undefined;
    const currentFileCount = this.portalFiles$.value.length;

    if (isModalOpen) {
      // Modal is open - user is viewing files, no need to show notification
      this.hasNewFiles$.next(false);
    } else {
      // Modal is closed - check if there are new files compared to last seen count
      const hasNewFiles = currentFileCount > this.lastSeenFileCount;
      this.hasNewFiles$.next(hasNewFiles);
    }
  }

  private onPortalFileListChange(event: {
    files: FileListItem[];
    previousFiles: FileListItem[];
    cwd: string;
  }): void {
    this.zone.run(() => {
      // Update the file list
      this.portalFiles$.next(event.files || []);
      // Update hasNewFiles based on centralized logic
      this.updateHasNewFiles();
    });
  }
}
